import React from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  pointerWithin
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove
} from '@dnd-kit/sortable';
import { Menu, Item, Submenu } from 'react-contexify';
import 'react-contexify/dist/ReactContexify.css';
import './App.css';

import API from './api';
import { MENU_ID, COLUMN_MENU_ID, containerOrder, LEROY_FORBIDDEN_HEADERS, buildInitialColumns } from './constants';
import { formatHour } from './utils/formatters';
import { parseCSVProper, cleanCell, detectRole, extractCity } from './utils/csvParser';
import { useAuth } from './hooks/useAuth';
import { useSaves } from './hooks/useSaves';
import { AuthBar } from './components/AuthBar';
import { SiteSwitch } from './components/SiteSwitch';
import { TaskFilter } from './components/TaskFilter';
import { AddTaskForm } from './components/AddTaskForm';
import { EditModal } from './components/EditModal';
import { CsvImportModal } from './components/CsvImportModal';
import { Column } from './components/Column';

function formatTimeAgo(date) {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'il y a quelques secondes';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
  const hours = Math.floor(minutes / 60);
  return `il y a ${hours} heure${hours > 1 ? 's' : ''}`;
}

function SaveNotification({ notif, onDismiss }) {
  const [, setTick] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="save-notification">
      <div className="save-notification-icon">&#9888;</div>
      <div>
        <strong>{notif.message}</strong>
        {notif.by && <div className="save-notification-by">par {notif.by}</div>}
        <div className="save-notification-time">{formatTimeAgo(notif.savedAt)}</div>
      </div>
      <button onClick={() => onDismiss(notif.id)} className="save-notification-close">
        &times;
      </button>
    </div>
  );
}

function App() {
  const [columns, setColumns] = React.useState(buildInitialColumns);
  const [activeId, setActiveId] = React.useState(null);
  const [editingItem, setEditingItem] = React.useState(null);

  const [site, setSite] = React.useState('beziers');
  const [taskFilter, setTaskFilter] = React.useState('all'); // all | leroy | R | P | RP

  // import CSV
  const [csvHeaders, setCsvHeaders] = React.useState([]);
  const [csvRows, setCsvRows] = React.useState([]);
  const [csvSelection, setCsvSelection] = React.useState({});
  const [csvRoles, setCsvRoles] = React.useState({});
  const [showCsvMapping, setShowCsvMapping] = React.useState(false);
  const [csvProfile, setCsvProfile] = React.useState('bricoCasto');

  const [ctxMenu, setCtxMenu] = React.useState({
    id: null,
    isDeparture: false,
    isTask: false,
    showLoadKg: true,
    dateADate: false,
    reprise: 0,
    cornerTag: ''
  });

  const {
    user,
    authEmail, setAuthEmail,
    authPassword, setAuthPassword,
    initials,
    canEdit,
    handleLogin,
    handleLogout
  } = useAuth();

  const {
    saveList,
    saveTitle, setSaveTitle,
    saveColor, setSaveColor,
    activeSave, setActiveSave,
    fetchSaves,
    initPrevSaves,
    handleSave,
    handleDeleteSave,
    applySave,
    persistActiveSaveSilently,
    saveNotifications,
    dismissNotification
  } = useSaves({ columns, setColumns, canEdit, site });

  React.useEffect(() => {
    fetchSaves(site);
    initPrevSaves(site);
    setActiveSave(null);
    setColumns(buildInitialColumns());
  }, [site, fetchSaves, initPrevSaves]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 10,
        tolerance: 5
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const isLeroyItem = React.useCallback((item) => {
    const order = String(item?.order_number || '').toLowerCase();
    const isBrico = order.includes('brico');
    const isCasto = order.includes('casto');
    return /^[\da-z]{3,}-/i.test(order) && !isBrico && !isCasto;
  }, []);

  const applyTaskFilter = React.useCallback((items) => {
    if (taskFilter === 'all') return items;

    return items.filter((item) => {
      if (item?.type === 'departure') return true; // on ne filtre pas les départs
      if (taskFilter === 'leroy') return isLeroyItem(item);
      if (taskFilter === 'R') return item?.cornerTag === 'R';
      if (taskFilter === 'P') return item?.cornerTag === 'P';
      if (taskFilter === 'RP') return item?.cornerTag === 'R' || item?.cornerTag === 'P';
      return true;
    });
  }, [taskFilter, isLeroyItem]);

  const canEditWithFilter = canEdit && taskFilter === 'all';

  // résumé commandes par enseigne
  const summary = React.useMemo(() => {
    const allOrders = containerOrder.flatMap((key) => columns[key].items);

    let leroyCount = 0;
    let bricoCount = 0;
    let castoCount = 0;
    let edCount = 0;

    allOrders.forEach((item) => {
      const order = String(item.order_number || '').toLowerCase();

      const isBrico = order.includes('brico');
      const isCasto = order.includes('casto');
      const isLeroy = /^[\da-z]{3,}-/i.test(order) && !isBrico && !isCasto;
      const isEd = order.startsWith('ed'); // EDxxxx...

      if (isLeroy) leroyCount++;
      if (isBrico) bricoCount++;
      if (isCasto) castoCount++;
      if (isEd) edCount++;
    });

    return {
      'Leroy Merlin': leroyCount,
      Brico: bricoCount,
      Casto: castoCount,
      ED: edCount,
      total: allOrders.length
    };
  }, [columns]);

  // pour bouton clic droit "Ajouter un tag (R) ou (P)" sur les commandes (hors départs)
  const setTaskCornerTag = async (id, tag) => {
    if (!canEdit) return;

    // retrouve l'item + sa colonne
    let foundItem = null;
    let foundKey = null;

    for (const key of containerOrder) {
      const it = columns[key].items.find((i) => String(i.id) === String(id));
      if (it) {
        foundItem = it;
        foundKey = key;
        break;
      }
    }
    if (!foundItem || !foundKey) return;

    // sécurité: pas sur les departures
    if (foundItem.type === 'departure') return;

    // Si c'est une tâche "locale" non enregistrée en DB (id type base- ou temporaire),
    // on peut juste modifier l'UI (et la pastille restera "non sauvegardé")
    // Toggle : si le tag est déjà actif, on l'enlève
    const newTag = foundItem.cornerTag === tag ? '' : tag;

    const isDbId = /^[0-9]+$/.test(String(foundItem.id));
    if (!isDbId) {
      setColumns((prev) => {
        const next = { ...prev };
        next[foundKey] = {
          ...next[foundKey],
          items: next[foundKey].items.map((i) =>
            String(i.id) === String(id) ? { ...i, cornerTag: newTag, isTemporary: true } : i
          )
        };
        return next;
      });
      return;
    }

    // ✅ Update DB (task_versions) -> PAS de création de sauvegarde
    const updated = {
      ...foundItem,
      cornerTag: newTag
    };

    try {
      await API.put(`/task/${foundItem.id}`, updated);
    } catch (err) {
      console.error(err);
      alert("Erreur update DB (reconnecte-toi si besoin)");
      return;
    }

    // ✅ Update UI
    setColumns((prev) => {
      const next = { ...prev };
      next[foundKey] = {
        ...next[foundKey],
        items: next[foundKey].items.map((i) =>
          String(i.id) === String(id)
            ? { ...i, cornerTag: newTag, isTemporary: false }
            : i
        )
      };
      persistActiveSaveSilently(next);
      return next;
    });
  };

  //bouton clic droit case a cocher pour afficher le poids
  const toggleDepartureShowKg = (id) => {
    if (!canEdit) return;

    setColumns((prev) => {
      const next = { ...prev };

      for (const key of containerOrder) {
        const items = next[key].items;
        const idx = items.findIndex((i) => String(i.id) === String(id));
        if (idx === -1) continue;

        const it = items[idx];
        if (it.type !== 'departure') return prev; // sécurité

        const updated = {
          ...it,
          showLoadKg: !(it.showLoadKg ?? true),
          isTemporary: true
        };

        const newItems = [...items];
        newItems[idx] = updated;

        next[key] = { ...next[key], items: newItems };

        // ✅ si c'est une "case de base" => persister dans la save active comme tu fais déjà
        persistActiveSaveSilently(next);

        return next;
      }

      return prev;
    });
  };

  // toggle tag "date à date" (violet) sur les courses
  const toggleTaskDateADate = async (id) => {
    if (!canEdit) return;

    let foundItem = null;
    let foundKey = null;

    for (const key of containerOrder) {
      const it = columns[key].items.find((i) => String(i.id) === String(id));
      if (it) { foundItem = it; foundKey = key; break; }
    }
    if (!foundItem || !foundKey) return;
    if (foundItem.type === 'departure') return;

    const newVal = !foundItem.dateADate;
    const isDbId = /^[0-9]+$/.test(String(foundItem.id));

    if (isDbId) {
      const updated = { ...foundItem, dateADate: newVal };
      try {
        await API.put(`/task/${foundItem.id}`, updated);
      } catch (err) {
        console.error(err);
        alert("Erreur update DB (reconnecte-toi si besoin)");
        return;
      }
      setColumns((prev) => {
        const next = { ...prev };
        next[foundKey] = {
          ...next[foundKey],
          items: next[foundKey].items.map((i) =>
            String(i.id) === String(id)
              ? { ...i, dateADate: newVal, isTemporary: false }
              : i
          )
        };
        persistActiveSaveSilently(next);
        return next;
      });
    } else {
      setColumns((prev) => {
        const next = { ...prev };
        next[foundKey] = {
          ...next[foundKey],
          items: next[foundKey].items.map((i) =>
            String(i.id) === String(id)
              ? { ...i, dateADate: newVal, isTemporary: true }
              : i
          )
        };
        return next;
      });
    }
  };

  // toggle tag "Reprise" (jaune) sur les courses
  const toggleTaskReprise = async (id) => {
    if (!canEdit) return;

    let foundItem = null;
    let foundKey = null;

    for (const key of containerOrder) {
      const it = columns[key].items.find((i) => String(i.id) === String(id));
      if (it) { foundItem = it; foundKey = key; break; }
    }
    if (!foundItem || !foundKey) return;
    if (foundItem.type === 'departure') return;

    const newVal = foundItem.reprise > 0 ? 0 : 1;
    const isDbId = /^[0-9]+$/.test(String(foundItem.id));

    if (isDbId) {
      const updated = { ...foundItem, reprise: newVal };
      try {
        await API.put(`/task/${foundItem.id}`, updated);
      } catch (err) {
        console.error(err);
        alert("Erreur update DB (reconnecte-toi si besoin)");
        return;
      }
      setColumns((prev) => {
        const next = { ...prev };
        next[foundKey] = {
          ...next[foundKey],
          items: next[foundKey].items.map((i) =>
            String(i.id) === String(id)
              ? { ...i, reprise: newVal, isTemporary: false }
              : i
          )
        };
        persistActiveSaveSilently(next);
        return next;
      });
    } else {
      setColumns((prev) => {
        const next = { ...prev };
        next[foundKey] = {
          ...next[foundKey],
          items: next[foundKey].items.map((i) =>
            String(i.id) === String(id)
              ? { ...i, reprise: newVal, isTemporary: true }
              : i
          )
        };
        return next;
      });
    }
  };

  const handleDuplicate = (id) => {
    if (!canEdit) return;

    // retrouve l'item
    let foundItem = null;
    let foundKey = null;

    for (const key of containerOrder) {
      const it = columns[key].items.find((i) => String(i.id) === String(id));
      if (it) {
        foundItem = it;
        foundKey = key;
        break;
      }
    }
    if (!foundItem || !foundKey) return;

    // clone
    const cloned = {
      ...foundItem,
      id: Date.now() + Math.random(),
      isTemporary: true,
      // si tu veux éviter de dupliquer un tag R/P sur des departures, tu peux laisser tel quel
    };

    setColumns((prev) => {
      const next = { ...prev };
      const list = [...next[foundKey].items];

      const idx = list.findIndex((i) => String(i.id) === String(id));
      list.splice(idx === -1 ? list.length : idx + 1, 0, cloned);

      next[foundKey] = { ...next[foundKey], items: list };
      return next;
    });
  };

  const handleAdd = (e) => {
    e.preventDefault();
    if (!canEdit) {
      alert('Tu es en lecture seule (viewer ou non connecté)');
      return;
    }

    const payload = {
      id: Date.now(),
      order_number: e.target.order_number.value,
      city: e.target.city.value,
      weight: parseFloat(e.target.weight.value),
      note: e.target.note.value,
      column_key: 'unassigned',
      position: 0,
      isTemporary: true
    };

    setColumns((prev) => ({
      ...prev,
      unassigned: {
        ...prev.unassigned,
        items: [...prev.unassigned.items, payload]
      }
    }));

    e.target.reset();
  };

  const handleEdit = (id) => {
    if (!canEdit) return;
    for (const key of containerOrder) {
      const found = columns[key].items.find((i) => i.id === id);
      if (found) {
        setEditingItem(found);
        break;
      }
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    const form = e.target;

    const updated = {
      ...editingItem,
      order_number: form.order_number.value,
      city: editingItem.type === 'departure' ? (form.city.value || '') : form.city.value,
      weight: parseFloat(form.weight.value),
      note: form.note.value,
      cornerTag: editingItem.cornerTag || ''   // ✅ garde le valeur du tag R/P si existant, sinon vide
    };

    // ✅ case de base : update uniquement côté React, pas en DB
    if (editingItem.type === 'departure') {
      // update objet
      const nextUpdated = { ...updated };

      // ✅ si departure en DB => PUT DB, et pas de pastille
      if (/^[0-9]+$/.test(String(editingItem.id))) {
        try {
          await API.put(`/task/${editingItem.id}`, nextUpdated);
        } catch (err) {
          console.error(err);
          alert('Erreur lors de la sauvegarde en base (droits ?) RECONNECTE TOI !!!');
          return;
        }

        setColumns((prev) => {
          const next = { ...prev };
          next[editingItem.column_key] = {
            ...next[editingItem.column_key],
            items: next[editingItem.column_key].items.map((i) =>
              String(i.id) === String(editingItem.id)
                ? { ...nextUpdated, isTemporary: false }
                : i
            )
          };
          persistActiveSaveSilently(next);
          return next;
        });

        setEditingItem(null);
        return;
      }

      // ✅ sinon (departure local/base) => update UI
      const next = { ...columns };
      next[editingItem.column_key] = {
        ...next[editingItem.column_key],
        items: next[editingItem.column_key].items.map((i) =>
          String(i.id) === String(editingItem.id)
            ? { ...nextUpdated, isTemporary: false } // pas de pastille si on a save active
            : i
        )
      };

      setColumns(next);
      setEditingItem(null);

      // si une save est active => on persiste (sinon ça reste local)
      if (activeSave) {
        persistActiveSaveSilently(next);
      } else {
        // si tu veux absolument la pastille quand il n'y a pas de save active :
        // remets isTemporary:true ici.
      }

      return;
    }

    try {
      await API.put(`/task/${editingItem.id}`, updated);
    } catch (err) {
      alert('Erreur lors de la sauvegarde en base (droits ?) RECONNECTE TOI !!!');
      console.error(err);
      return;
    }

    setColumns((prev) => {
      const next = { ...prev };
      next[editingItem.column_key] = {
        ...next[editingItem.column_key],
        items: next[editingItem.column_key].items.map((i) =>
          i.id === editingItem.id ? updated : i
        )
      };
      persistActiveSaveSilently(next);
      return next;
    });

    setEditingItem(null);
  };

  const isDbId = (id) => /^[0-9]+$/.test(String(id));

  const handleDeduplication = async () => {
    if (!canEdit) return;

    const norm = (s) => String(s || '').trim().toLowerCase();

    // Collecte tous les order_numbers présents dans les camions (hors departure)
    const truckOrderNumbers = new Set();
    for (const key of containerOrder.slice(1)) {
      for (const item of columns[key].items) {
        if (item.type !== 'departure') {
          truckOrderNumbers.add(norm(item.order_number));
        }
      }
    }

    // Trouve les items en attente dont le order_number est déjà dans un camion
    // ou en double dans la liste en attente elle-même (garde le premier)
    const seenInUnassigned = new Set();
    const toRemove = [];
    for (const item of columns.unassigned.items) {
      const key = norm(item.order_number);
      if (truckOrderNumbers.has(key) || seenInUnassigned.has(key)) {
        toRemove.push(item);
      } else {
        seenInUnassigned.add(key);
      }
    }

    if (toRemove.length === 0) {
      alert('Aucun doublon trouvé.');
      return;
    }

    const list = toRemove.map((i) => `• #${i.order_number} – ${i.city}`).join('\n');
    const confirmed = window.confirm(
      `${toRemove.length} doublon(s) trouvé(s) dans "En attente" :\n\n${list}\n\nSupprimer ces commandes de la liste En attente ?`
    );
    if (!confirmed) return;

    // Supprimer en DB si enregistré
    for (const item of toRemove) {
      if (isDbId(item.id)) {
        try {
          await API.delete(`/task/${item.id}`);
        } catch (err) {
          console.error('Erreur suppression doublon DB :', err);
        }
      }
    }

    // Supprimer de l'UI
    const removeIds = new Set(toRemove.map((i) => String(i.id)));
    setColumns((prev) => ({
      ...prev,
      unassigned: {
        ...prev.unassigned,
        items: prev.unassigned.items.filter((i) => !removeIds.has(String(i.id)))
      }
    }));
  };

  const handleDelete = async (id) => {
    if (!canEdit) return;

    // retrouve l'item + colonne
    let foundItem = null;
    let foundKey = null;

    for (const key of containerOrder) {
      const it = columns[key].items.find((i) => String(i.id) === String(id));
      if (it) {
        foundItem = it;
        foundKey = key;
        break;
      }
    }
    if (!foundItem || !foundKey) return;

    // ✅ Si c'est en DB => delete DB
    if (isDbId(foundItem.id)) {
      try {
        await API.delete(`/task/${foundItem.id}`);
      } catch (err) {
        console.error('Erreur lors de la suppression :', err);
        alert('Erreur lors de la suppression (droits ?) RECONNECTE TOI !!!');
        return;
      }

      // update UI
      setColumns((prev) => {
        const next = { ...prev };
        next[foundKey] = {
          ...next[foundKey],
          items: next[foundKey].items.filter((i) => String(i.id) !== String(id))
        };
        return next;
      });

      return;
    }

    // ✅ Sinon (departure local/base) => suppression UI + persistance save active si dispo
    setColumns((prev) => {
      const next = { ...prev };
      next[foundKey] = {
        ...next[foundKey],
        items: next[foundKey].items.filter((i) => String(i.id) !== String(id))
      };
      return next;
    });

    if (activeSave) {
      // persiste dans la sauvegarde en cours (sinon ça restera local)
      const next = { ...columns };
      next[foundKey] = {
        ...next[foundKey],
        items: next[foundKey].items.filter((i) => String(i.id) !== String(id))
      };
      persistActiveSaveSilently(next);
    }
  };

  const handleDragStart = ({ active }) => {
    if (!canEdit) return;
    setActiveId(active.id);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!canEdit) return;
    if (!over) return;

    const from = active.data.current.container;
    const to = over.data.current?.container || over.id;
    if (!from || !to) return;

    setColumns((prev) => {
      const src = [...prev[from].items];
      const dst = [...prev[to].items];
      const oldIndex = src.findIndex((i) => String(i.id) === String(active.id));

      // même colonne -> on réordonne + pastille non sauvegardé
      if (from === to) {
        const newIndex = dst.findIndex((i) => String(i.id) === String(over.id));
        const newItems = arrayMove(src, oldIndex, newIndex === -1 ? src.length - 1 : newIndex);

        const newItemsWithFlag = newItems.map((item) =>
          String(item.id) === String(active.id)
            ? { ...item, isTemporary: true }
            : item
        );

        return {
          ...prev,
          [from]: {
            ...prev[from],
            items: newItemsWithFlag
          }
        };
      }

      // changement de colonne -> déjà non sauvegardé
      const [moved] = src.splice(oldIndex, 1);
      moved.isTemporary = true;

      const newIndex = dst.findIndex((i) => String(i.id) === String(over.id));
      dst.splice(newIndex === -1 ? dst.length : newIndex, 0, moved);

      return {
        ...prev,
        [from]: { ...prev[from], items: src },
        [to]: { ...prev[to], items: dst }
      };
    });
  };

  const activeItem = React.useMemo(() => {
    for (const key of containerOrder) {
      const found = columns[key].items.find((i) => String(i.id) === String(activeId));
      if (found) return found;
    }
    return null;
  }, [activeId, columns]);

  // --------- IMPORT CSV ---------

  const recomputeCsvRoles = (headers, profile) => {
    if (!headers || headers.length === 0) return;
    const roles = {};
    const selection = {};

    headers.forEach((h) => {
      // Pour Leroy : on ignore certaines colonnes complètement
      if (profile === 'leroy') {
        const lower = h.toLowerCase();
        if (LEROY_FORBIDDEN_HEADERS.some((f) => lower.includes(f))) {
          return; // on ne leur attribue PAS de rôle
        }
      }

      const role = detectRole(h, profile);
      if (role) {
        roles[h] = role;
        selection[h] = true;
      }
    });

    setCsvRoles(roles);
    setCsvSelection(selection);
  };

  const handleCSVUpload = (e) => {
    if (!canEdit) {
      alert('Import CSV réservé à un admin');
      return;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (event) => {
      const text = event.target.result;
      if (!text) return;

      const { rows } = parseCSVProper(text);
      if (rows.length === 0) return;

      const headers = rows[0].map((h) => cleanCell(h));
      const csvRowsParsed = rows.slice(1).map((row) =>
        row.map((v) => cleanCell(v))
      );

      setCsvHeaders(headers);
      setCsvRows(csvRowsParsed);
      recomputeCsvRoles(headers, csvProfile);
      setShowCsvMapping(true);
    };

    reader.readAsText(file, 'utf-8');
  };

  const toggleCsvSelection = (header) => {
    setCsvSelection((prev) => ({
      ...prev,
      [header]: !prev[header]
    }));
  };

  const applyCsvImport = () => {
    if (!canEdit) return;

    const newItems = csvRows.map((row) => {
      let sender = '';
      let taskRef = '';
      let clientRep = '';
      let city = '';
      let weight = 0;
      let categories = '';
      let serviceType = '';
      let start = '';
      let end = '';
      let backOffice = '';
      let reprise = 0;

      csvHeaders.forEach((header, index) => {
        const role = csvRoles[header];
        if (!role) return;
        if (!csvSelection[header]) return;

        const value = row[index] ?? '';

        switch (role) {
          case 'sender':
            sender = value;
            break;
          case 'taskRef':
            taskRef = value;
            break;
          case 'clientRep':
            clientRep = value;
            break;
          case 'city':
            city = csvProfile === 'leroy' ? extractCity(value) : value;
            break;
          case 'weight':
            weight += parseFloat(String(value).replace(',', '.')) || 0;
            break;
          case 'backOffice':
            if (value && String(value).toUpperCase() !== 'N/A') {
              backOffice = value;
            }
            break;
          case 'categories':
            categories = value;

            if (csvProfile === 'leroy') {
              const lower = String(value).toLowerCase();
              if (lower.includes('room_choice')) categories = 'pièce du choix';
              else if (lower.includes('sidewalk')) categories = 'pied du camion';
            }

            if (csvProfile === 'goxl') {
              // Statut est multiligne → on garde seulement la 1ère ligne
              const firstLine = String(value).split(/\r?\n/)[0].trim();
              const lower = firstLine.toLowerCase();
              if (lower.includes('trottoir') || lower.includes('sidewalk')) categories = 'pied du camion';
              else if (lower.includes('piece') || lower.includes('pièce') || lower.includes('choix')) categories = 'pièce du choix';
              else categories = firstLine; // fallback : 1ère ligne brute
              // Si le statut indique une reprise, activer le tag Reprise
              if (lower.includes('reprise')) reprise = 1;
            }
            break;
          case 'serviceType':
            serviceType = value;
            break;
          case 'start':
            start = value;
            break;
          case 'end':
            end = value;
            break;
          case 'reprise':
            reprise += parseFloat(String(value).replace(',', '.')) || 0;
            break;
          default:
            break;
        }
      });

      // 👉 Gestion du Type de Service pour Brico / Casto
      // Si "Type de Service" (serviceType) contient "pickup",
      // on ajoute "Reprise" juste après l'expéditeur
      let repriseFlag = '';
      const cleanService = (serviceType || '').toLowerCase().replace(/[^a-z]/g, '');
      if (csvProfile === 'bricoCasto' && cleanService.includes('pickup')) {
        repriseFlag = 'Reprise';
      }

      // Construction du numéro de commande
      const orderParts = [sender, repriseFlag, taskRef, clientRep].filter(Boolean);
      const order_number = orderParts.join('-');

      const startTxt = formatHour(start);
      const endTxt = formatHour(end);
      const noteParts = [];
      if (categories) noteParts.push(categories);
      if (startTxt || endTxt) {
        const timeText = `${startTxt || ''}${startTxt && endTxt ? '/' : ''}${endTxt || ''}`;
        noteParts.push(timeText);
      }
      const note = noteParts.join(' | ');

      const bo = String(backOffice || '').trim().toUpperCase();

      let cornerTag = '';
      if (bo === 'LMFR-SUDO-158-RIVE-STORE' || bo === 'LMFR-SUDO-158-RIVE-SFS') {
        cornerTag = 'R';
      } else if (bo === 'LMFR-SUDO-171-PERP-SFS' || bo === 'LMFR-SUDO-171-PERP-STORE') {
        cornerTag = 'P';
      }

      return {
        id: Date.now() + Math.random(),
        order_number,
        city,
        weight,
        note,
        backOffice,
        cornerTag,
        reprise,
        column_key: 'unassigned',
        position: 0,
        isTemporary: true
      };
    });

    setColumns((prev) => ({
      ...prev,
      unassigned: {
        ...prev.unassigned,
        items: [...prev.unassigned.items, ...newItems]
      }
    }));

    setShowCsvMapping(false);
  };

  const relevantHeaders = csvHeaders.filter((h) => {
    if (csvProfile === 'leroy') {
      const lower = h.toLowerCase();
      if (LEROY_FORBIDDEN_HEADERS.some((f) => lower.includes(f))) {
        return false; // caché dans la table
      }
    }
    return csvRoles[h];
  });

  return (
    <div className="app-root">
      {/* Notifications de sauvegarde */}
      {saveNotifications.length > 0 && (
        <div className="save-notifications">
          {saveNotifications.map((n) => (
            <SaveNotification key={n.id} notif={n} onDismiss={dismissNotification} />
          ))}
        </div>
      )}

      <header className="app-header">
        <img src="/logo.png" alt="Logo" className="app-logo" />
        <h1 className="app-title">Gestion des commandes</h1>
      </header>

      {/* Switch de magasin */}
      <SiteSwitch site={site} setSite={setSite} />

      {/* Barre d'authentification */}
      <AuthBar
        user={user}
        initials={initials}
        canEdit={canEdit}
        authEmail={authEmail}
        setAuthEmail={setAuthEmail}
        authPassword={authPassword}
        setAuthPassword={setAuthPassword}
        handleLogin={handleLogin}
        handleLogout={handleLogout}
      />

      {/* Import CSV + Dédoublonnage */}
      {canEdit && (
        <div style={{ marginBottom: '16px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '12px' }}>
          <button
            onClick={() => document.getElementById('csvInput').click()}
            style={{ padding: '8px 14px' }}
          >
            Importer un fichier CSV
          </button>
          <button
            onClick={handleDeduplication}
            style={{ padding: '8px 14px', background: 'orange', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            Supprimer les doublons
          </button>
          <input
            id="csvInput"
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleCSVUpload}
          />
        </div>
      )}

      {activeSave && (
        <div
          style={{
            margin: '16px auto',
            padding: '10px 20px',
            backgroundColor: activeSave.color || '#888',
            color: '#fff',
            fontWeight: 'bold',
            borderRadius: 8,
            width: 'fit-content',
            boxShadow: '0 0 8px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <span>Sauvegarde en cours :</span>
          <span style={{ fontWeight: 700 }}>{activeSave.title}</span>
          <button
            onClick={() => {
              setActiveSave(null);
              setColumns(buildInitialColumns());
            }}
            style={{
              marginLeft: 8,
              background: '#fff',
              color: '#000',
              border: 'none',
              padding: '4px 8px',
              borderRadius: 4,
              cursor: 'pointer'
            }}
          >
            Réinitialiser
          </button>
        </div>
      )}

      {/* Formulaire d'ajout manuel */}
      <AddTaskForm canEdit={canEdit} handleAdd={handleAdd} />

      {/* Sauvegarde */}
      {canEdit && (
        <div className="save-row">
          <input
            type="text"
            placeholder="Titre sauvegarde"
            value={saveTitle}
            onChange={(e) => setSaveTitle(e.target.value)}
            style={{ width: '260px', padding: '6px' }}
          />
          <div style={{ display: 'flex', gap: '10px' }}>
            {['red', 'blue'].map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setSaveColor(color)}
                title={color === 'red' ? 'Rouge = Tournée' : 'Bleu = À placer'}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: color,
                  border: saveColor === color ? '3px solid black' : '2px solid gray',
                  position: 'relative'
                }}
              >
                {saveColor === color && (
                  <span
                    style={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      background: '#000',
                      color: '#fff',
                      fontSize: 12,
                      padding: '2px 6px',
                      borderRadius: 10
                    }}
                  >
                    ✓
                  </span>
                )}
              </button>
            ))}
          </div>
          <button onClick={handleSave}>Enregistrer</button>
        </div>
      )}

      {/* Résumé commandes */}
      <div className="summary">
        <strong>Commandes : </strong>
        Leroy Merlin : {summary['Leroy Merlin']} | Brico : {summary['Brico']} | Casto :{' '}
        {summary['Casto']} | ED : {summary['ED']}
      </div>

      {/* Filtre tâches */}
      <TaskFilter taskFilter={taskFilter} setTaskFilter={setTaskFilter} />

      {/* Liste des sauvegardes */}
      <div className="saves-list">
        {saveList.map((save) => (
          <div key={save.id} className="save-item">
            <div
              style={{
                width: 20,
                height: 20,
                background: save.color || '#ccc',
                borderRadius: 4
              }}
            />
            <button onClick={() => applySave(save)}>Charger : {save.title}</button>
            {canEdit && (
              <button onClick={() => handleDeleteSave(save.id)} style={{ color: 'red' }}>
                Supprimer
              </button>
            )}
          </div>
        ))}
      </div>

      {/* DnD board */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        layoutMeasuring={{
          strategy: 'always',
          frequency: 5
        }}
      >
        <SortableContext items={['unassigned']} strategy={horizontalListSortingStrategy}>
          <div className="board-layout">
            <div className="unassigned-wrapper">
              {(() => {
                const filteredUnassigned = applyTaskFilter(columns.unassigned.items);

                return (
                  <SortableContext
                    items={
                      filteredUnassigned.length === 0
                        ? ['__placeholder__']
                        : filteredUnassigned.map((i) => String(i.id))
                    }
                    strategy={verticalListSortingStrategy}
                  >
                    <Column
                      id="unassigned"
                      column={{ ...columns.unassigned, items: filteredUnassigned }}
                      allItems={columns.unassigned.items}     // ✅ total calculé sur tout
                      canEdit={canEditWithFilter}
                      setCtxMenu={setCtxMenu}
                    />
                  </SortableContext>
                );
              })()}
            </div>

            <div className="trucks-grid">
              {containerOrder.slice(1).map((key) => {
                const filteredItems = applyTaskFilter(columns[key].items);

                return (
                  <SortableContext
                    key={key}
                    items={filteredItems.map((i) => String(i.id))}
                    strategy={verticalListSortingStrategy}
                  >
                    <Column
                      id={key}
                      column={{ ...columns[key], items: filteredItems }}
                      allItems={columns[key].items}           // ✅ total calculé sur tout
                      canEdit={canEditWithFilter}
                      setCtxMenu={setCtxMenu}
                    />
                  </SortableContext>
                );
              })}
            </div>
          </div>
        </SortableContext>

        <DragOverlay>
          {activeItem && (
            <div
              style={{
                padding: '12px',
                background: '#fffacd',
                border: '2px solid orange',
                borderRadius: 4,
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                opacity: 0.95,
                fontWeight: 'bold'
              }}
            >
              #{activeItem.order_number} – {activeItem.city} ({activeItem.weight} kg)
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Menu clic droit : seulement utile si canEdit (mais protégé dans SortableItem) */}
      <Menu id={MENU_ID} animation="fade">
        <Item
          disabled={!ctxMenu.isDeparture}
          onClick={() => ctxMenu.id && toggleDepartureShowKg(ctxMenu.id)}
        >
          {(ctxMenu.showLoadKg ? '☑' : '☐') + ' Afficher le poids'}
        </Item>

        <Submenu label="Ajouter" disabled={!ctxMenu.isTask}>
          <Item onClick={() => ctxMenu.id && setTaskCornerTag(ctxMenu.id, 'R')}>
            {(ctxMenu.cornerTag === 'R' ? '☑' : '☐') + ' (R)'}
          </Item>
          <Item onClick={() => ctxMenu.id && setTaskCornerTag(ctxMenu.id, 'P')}>
            {(ctxMenu.cornerTag === 'P' ? '☑' : '☐') + ' (P)'}
          </Item>
          <Item onClick={() => ctxMenu.id && toggleTaskDateADate(ctxMenu.id)}>
            {(ctxMenu.dateADate ? '☑' : '☐') + ' date à date'}
          </Item>
          <Item onClick={() => ctxMenu.id && toggleTaskReprise(ctxMenu.id)}>
            {(ctxMenu.reprise > 0 ? '☑' : '☐') + ' Reprise'}
          </Item>
        </Submenu>

        <Item onClick={({ props }) => handleEdit(props.id)}>Éditer</Item>
        <Item onClick={({ props }) => handleDuplicate(props.id)}>Dupliquer</Item>
        <Item onClick={({ props }) => handleDelete(props.id)}>Supprimer</Item>
      </Menu>

      {/* Menu clic droit sur titre colonne */}
      <Menu id={COLUMN_MENU_ID} animation="fade">
        <Item onClick={({ props }) => props?.onCapture?.()}>
          📸 Capture
        </Item>
      </Menu>

      {/* Modal édition */}
      <EditModal
        editingItem={editingItem}
        setEditingItem={setEditingItem}
        canEdit={canEdit}
        handleEditSubmit={handleEditSubmit}
      />

      {/* Fenêtre de mapping CSV */}
      <CsvImportModal
        showCsvMapping={showCsvMapping}
        setShowCsvMapping={setShowCsvMapping}
        canEdit={canEdit}
        csvProfile={csvProfile}
        setCsvProfile={setCsvProfile}
        csvHeaders={csvHeaders}
        csvRows={csvRows}
        csvRoles={csvRoles}
        csvSelection={csvSelection}
        toggleCsvSelection={toggleCsvSelection}
        recomputeCsvRoles={recomputeCsvRoles}
        applyCsvImport={applyCsvImport}
        relevantHeaders={relevantHeaders}
      />
    </div>
  );
}

export default App;
